import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  makeStyles,
  Button,
} from "@material-ui/core";
import {
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
} from "@material-ui/icons";
import api from "../../services/api";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTimezone } from "../../hooks/useTimezone";

const POLL_MS = 30000;

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", color: "#25D366", Icon: WhatsAppIcon },
  { key: "telegram", label: "Telegram", color: "#229ED9", Icon: TelegramIcon },
];

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  cardHeader: {
    backgroundColor: theme.mode === "dark" ? "#111827" : "#f8fafc",
    color: theme.palette.text.primary,
    padding: theme.spacing(1.75, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px 8px 0 0",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  cardContent: {
    padding: theme.spacing(2),
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  headerActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  appMetricCard: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    minHeight: 112,
  },
  appMetricValue: {
    fontSize: 30,
    fontWeight: 800,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.75),
    lineHeight: 1.2,
  },
  appMetricLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },
  appMetricSubtext: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
  errorContainer: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.error.main,
  },
  healthList: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1),
  },
  healthItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.mode === "dark" ? "#111827" : "#ffffff",
  },
}));

const MetricCard = ({ classes, label, value, subtext, color }) => (
  <Box className={classes.appMetricCard}>
    <Typography className={classes.appMetricLabel}>{label}</Typography>
    <Typography className={classes.appMetricValue} style={{ color }}>
      {value}
    </Typography>
    {subtext && <Typography className={classes.appMetricSubtext}>{subtext}</Typography>}
  </Box>
);

const WhatsAppMetrics = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = React.useContext(AuthContext);
  const { formatDateTime } = useTimezone();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const application = metrics?.application;
  const dashboard = useMemo(() => {
    const tickets = application?.tickets || {};
    const messages = application?.messages || {};
    const users = application?.users || {};

    return {
      tickets,
      messages,
      users,
      byChannel: application?.byChannel || {},
      actionTickets: Number(tickets.open || 0) + Number(tickets.pending || 0),
      onlineUsers: Number(users.online || 0),
    };
  }, [application]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/performance/metrics");

      if (response.data?.success && response.data?.data) {
        setMetrics(response.data.data);
      }
    } catch (err) {
      console.error("Error cargando métricas:", err);
      setError("Error al cargar métricas del servidor");
      toast.error("Error al cargar métricas del servidor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
    const interval = setInterval(loadMetrics, POLL_MS);
    return () => clearInterval(interval);
  }, []);

  if (loading && !metrics) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: 16 }}>
          Cargando métricas de la aplicación...
        </Typography>
      </Box>
    );
  }

  if (error && !metrics) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h6">{error}</Typography>
        <Button variant="contained" color="primary" onClick={loadMetrics} style={{ marginTop: 16 }}>
          Reintentar
        </Button>
      </Box>
    );
  }

  const exportMetrics = () => {
    if (!metrics) {
      toast.error("No hay métricas para exportar");
      return;
    }
    const payload = {
      exportedAt: new Date().toISOString(),
      timestamp: metrics.timestamp || null,
      application: metrics.application || application || {},
    };
    const dataStr = JSON.stringify(payload, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `metricas-actividad-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Métricas exportadas");
  };

  if (!metrics || !application) {
    return (
      <Box className={classes.container}>
        <Typography variant="h6">No hay métricas disponibles</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.pageHeader}>
        <Box>
          <Typography variant="h5" style={{ fontWeight: 800 }}>
            Actividad por canal
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Tickets y mensajes de WhatsApp y Telegram, y agentes en línea.
            {user?.super || user?.profile === "super" ? " Vista global de todas las empresas." : ""} Salud de sesiones en Conexiones.
          </Typography>
        </Box>
        <Box className={classes.headerActions}>
          <Button size="small" variant="outlined" onClick={() => history.push("/dashboard/connections")}>
            Ver conexiones
          </Button>
          <Button size="small" startIcon={<ExportIcon />} onClick={exportMetrics} variant="outlined" disabled={!metrics}>
            Exportar
          </Button>
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadMetrics} variant="contained" color="primary" disabled={loading}>
            Actualizar
          </Button>
        </Box>
      </Box>

      <Box className={classes.summaryGrid}>
        <MetricCard
          classes={classes}
          label="Tickets por atender"
          value={dashboard.actionTickets.toLocaleString()}
          subtext={`${Number(dashboard.tickets.open || 0).toLocaleString()} abiertos · ${Number(
            dashboard.tickets.pending || 0
          ).toLocaleString()} pendientes`}
          color={dashboard.actionTickets > 0 ? "#B91C1C" : "#047857"}
        />
        <MetricCard
          classes={classes}
          label="Mensajes última hora"
          value={Number(dashboard.messages.lastHour || 0).toLocaleString()}
          subtext={`${dashboard.messages.perMinute || 0} por minuto`}
          color="#1D4ED8"
        />
        <MetricCard
          classes={classes}
          label="Usuarios en línea"
          value={dashboard.onlineUsers.toLocaleString()}
          subtext={`${Number(dashboard.users.total || 0).toLocaleString()} usuarios en total`}
          color="#047857"
        />
      </Box>

      <Box className={classes.contentGrid}>
        {CHANNELS.map((channel) => {
          const data = dashboard.byChannel[channel.key] || {};
          const connections = Number(data.connections || 0);
          const connected = Number(data.connected || 0);
          const waiting = Number(data.ticketsOpen || 0) + Number(data.ticketsPending || 0);
          return (
            <Card key={channel.key} className={classes.card}>
              <Box className={classes.cardHeader}>
                <channel.Icon style={{ color: channel.color }} />
                <Typography variant="h6" style={{ fontWeight: 700, flex: 1 }}>
                  {channel.label}
                </Typography>
                <Typography
                  variant="body2"
                  style={{ fontWeight: 800, color: connections && connected < connections ? "#B91C1C" : "#047857" }}
                >
                  {connections ? `${connected}/${connections} conectadas` : "Sin conexiones"}
                </Typography>
              </Box>
              <CardContent className={classes.cardContent}>
                <Box className={classes.metricGrid}>
                  <MetricCard
                    classes={classes}
                    label="Tickets por atender"
                    value={waiting.toLocaleString()}
                    subtext={`${Number(data.ticketsOpen || 0).toLocaleString()} abiertos · ${Number(
                      data.ticketsPending || 0
                    ).toLocaleString()} pendientes · ${Number(data.ticketsToday || 0).toLocaleString()} hoy`}
                    color={waiting > 0 ? "#B91C1C" : "#047857"}
                  />
                  <MetricCard
                    classes={classes}
                    label="Mensajes hoy"
                    value={Number(data.messagesToday || 0).toLocaleString()}
                    subtext={`${Number(data.messagesLastHour || 0).toLocaleString()} en la última hora`}
                    color={channel.color}
                  />
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Box textAlign="center" marginTop={2}>
        <Typography variant="caption" color="textSecondary">
          Última actualización: {formatDateTime(metrics.timestamp)} · cada 30 segundos
        </Typography>
      </Box>
    </Box>
  );
};

export default WhatsAppMetrics;
